/**
 * R84–R85 · Brand-allocation chart
 * ─────────────────────────────────
 * Rolls up `skill_breakdown[].assignee_id` × `brand_id` across all active +
 * draft packages, so leadership can see at a glance which team member owns
 * how much monthly delivery for each brand. Also shows a compact recent-
 * changes list so hand-offs are auditable.
 *
 * R85 additions:
 *  - Brand filter dropdown (scopes the scan to one brand).
 *  - Capacity guardrail — workspace setting `capacity_hours_default`;
 *    each user row over the cap is flagged in red and counted in the
 *    "Over capacity" summary tile. Leaders can edit the cap inline.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Users, Building2, Clock, RefreshCw, AlertTriangle, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

/** Deterministic HSL colour derived from a brand id so the chart is stable. */
function brandColor(brandId) {
  if (!brandId) return "hsl(220, 12%, 55%)";
  let hash = 0;
  for (const ch of brandId) hash = (hash * 31 + ch.charCodeAt(0)) & 0xffffffff;
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 52%)`;
}

export default function BrandAllocationChart({ wsId }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [trends, setTrends] = useState(null);              // R86 · capacity trends
  const [loading, setLoading] = useState(false);
  const [brandFilter, setBrandFilter] = useState("_all");   // R85
  const [capEditing, setCapEditing] = useState(false);      // R85
  const [capDraft, setCapDraft] = useState("");

  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const params = brandFilter && brandFilter !== "_all" ? { brand_id: brandFilter } : {};
      const [a, h, t] = await Promise.all([
        api.get(`/workspaces/${wsId}/packages/brand-allocation`, { params }),
        api.get(`/workspaces/${wsId}/packages/brand-allocation/history`, {
          params: { limit: 25, ...(brandFilter !== "_all" ? { brand_id: brandFilter } : {}) },
        }),
        api.get(`/workspaces/${wsId}/packages/capacity-trends`, { params: { months: 6 } }),
      ]);
      setData(a.data);
      setHistory(h.data?.events || []);
      setTrends(t.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load brand allocation"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, brandFilter]);

  const saveCapacity = async (val) => {
    const num = Math.max(0, Math.min(1000, Math.round(Number(val))));
    if (!Number.isFinite(num)) return setCapEditing(false);
    try {
      await api.put(`/workspaces/${wsId}/package-settings`, { capacity_hours_default: num });
      toast.success(num > 0 ? `Capacity guardrail set to ${num}h/mo` : "Capacity guardrail disabled");
      setCapEditing(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save capacity")); }
  };

  // R86 · Persist the per-member cap map to workspace settings. `newCaps` is
  // the full replacement dict (uid → hours). Empty value removes the entry.
  const saveMemberCaps = async (newCaps) => {
    try {
      await api.put(`/workspaces/${wsId}/package-settings`, { member_caps: newCaps });
      toast.success("Per-member cap saved");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save cap override")); }
  };

  const maxHours = useMemo(() => Math.max(1, ...((data?.user_totals || []).map((u) => u.monthly_hours))), [data]);
  const cellsByUser = useMemo(() => {
    const m = new Map();
    (data?.cells || []).forEach((c) => {
      if (!m.has(c.assignee_id)) m.set(c.assignee_id, []);
      m.get(c.assignee_id).push(c);
    });
    return m;
  }, [data]);
  const trendsByUser = useMemo(() => {
    const m = new Map();
    (trends?.series || []).forEach((s) => m.set(s.user_id, s.points || []));
    return m;
  }, [trends]);
  const overCapacityCount = useMemo(
    () => (data?.user_totals || []).filter((u) => u.over_capacity).length,
    [data],
  );

  if (!data && loading) {
    return <div className="text-sm text-muted-foreground py-8 text-center" data-testid="brand-alloc-loading">Loading brand allocation…</div>;
  }
  if (!data) return null;

  const totalHours = (data.brand_totals || []).reduce((s, b) => s + (b.monthly_hours || 0), 0);
  const brandCount = (data.brand_totals || []).length;
  const userCount = (data.user_totals || []).length;
  const cap = data.capacity_hours;

  return (
    <div className="space-y-6" data-testid="brand-allocation-chart">
      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Brand</span>
          <Select value={brandFilter} onValueChange={setBrandFilter}>
            <SelectTrigger className="h-8 w-56 rounded-none text-xs" data-testid="brand-alloc-brand-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All brands</SelectItem>
              {(data.brands || []).map((b) => (
                <SelectItem key={b.id} value={b.id} data-testid={`brand-alloc-brand-filter-option-${b.id}`}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Capacity cap</span>
          {capEditing ? (
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min={0}
                max={1000}
                value={capDraft}
                onChange={(e) => setCapDraft(e.target.value)}
                className="h-8 w-24 rounded-none text-xs"
                autoFocus
                data-testid="brand-alloc-capacity-input"
              />
              <Button type="button" size="sm" variant="outline" className="h-8 rounded-none" onClick={() => saveCapacity(capDraft)} data-testid="brand-alloc-capacity-save">
                <Check className="w-3 h-3" />
              </Button>
              <Button type="button" size="sm" variant="ghost" className="h-8 rounded-none" onClick={() => setCapEditing(false)} data-testid="brand-alloc-capacity-cancel">
                <X className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <button
              type="button"
              className="text-xs px-2 h-8 border border-border hover:border-primary/50 flex items-center gap-1"
              onClick={() => { setCapDraft(String(cap || "")); setCapEditing(true); }}
              data-testid="brand-alloc-capacity-edit"
              title="Click to edit workspace capacity cap"
            >
              {cap ? <><span className="font-mono">{cap}h</span><span className="text-muted-foreground">/mo</span></> : <span className="text-muted-foreground italic">disabled</span>}
              <Pencil className="w-3 h-3 ml-1 opacity-50" />
            </button>
          )}
        </div>
        <div className="ml-auto">
          <Button type="button" size="sm" variant="outline" onClick={load} disabled={loading} data-testid="brand-alloc-refresh" className="h-8 rounded-none text-[11px]">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <SummaryTile label="Packages scanned" value={data.packages_scanned} icon={<Building2 className="w-4 h-4" />} testid="brand-alloc-pkg-count" />
        <SummaryTile label="Team members" value={userCount} icon={<Users className="w-4 h-4" />} testid="brand-alloc-user-count" />
        <SummaryTile label="Brands covered" value={brandCount} icon={<Building2 className="w-4 h-4" />} testid="brand-alloc-brand-count" />
        <SummaryTile label="Assigned hours / mo" value={`${totalHours.toFixed(1)}h`} icon={<Clock className="w-4 h-4" />} testid="brand-alloc-hours" />
        <SummaryTile label="Unassigned / mo" value={`${(data.unassigned_hours || 0).toFixed(1)}h`} tone={data.unassigned_hours > 0 ? "amber" : ""} icon={<Clock className="w-4 h-4" />} testid="brand-alloc-unassigned" />
        <SummaryTile label="Over capacity" value={overCapacityCount} tone={overCapacityCount > 0 ? "rose" : ""} icon={<AlertTriangle className="w-4 h-4" />} testid="brand-alloc-over-capacity" />
      </div>

      <div className="flex items-center gap-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Monthly hours by member · brand</div>
      </div>

      {(data.user_totals || []).length === 0 ? (
        <Card className="rounded-none border-dashed">
          <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="brand-alloc-empty">
            No skill-row assignees on any active package yet.
            <div className="text-[11px] mt-1">Open a package → expand a line → skill breakdown → pick a default assignee.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.user_totals.map((u) => {
            const cells = (cellsByUser.get(u.assignee_id) || []).sort((a, b) => b.monthly_hours - a.monthly_hours);
            const widthPct = (u.monthly_hours / maxHours) * 100;
            const over = u.over_capacity;
            const trendPoints = trendsByUser.get(u.assignee_id) || [];
            return (
              <div
                key={u.assignee_id}
                className={`grid grid-cols-[260px_1fr_120px_100px] items-center gap-2 py-1 border-b border-border/40 ${over ? "bg-rose-50" : ""}`}
                data-testid={`brand-alloc-row-${u.assignee_id}`}
              >
                <div className="truncate text-xs flex items-center gap-1.5">
                  {over && (
                    <AlertTriangle className="w-3.5 h-3.5 text-rose-600 shrink-0" data-testid={`brand-alloc-over-${u.assignee_id}`} title={`Over ${u.cap_hours}h/mo capacity`} />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className={`font-semibold truncate ${over ? "text-rose-800" : ""}`}>{u.assignee_name}</div>
                    <div className={`text-[10px] ${over ? "text-rose-700" : "text-muted-foreground"}`}>
                      {u.brand_count} brand{u.brand_count === 1 ? "" : "s"}
                      {u.cap_hours ? ` · cap ${u.cap_hours}h` : ""}
                    </div>
                  </div>
                  <MemberCapEditor
                    userId={u.assignee_id}
                    override={u.cap_override}
                    fallbackCap={cap}
                    memberCaps={data.member_caps || {}}
                    onSave={(newCaps) => saveMemberCaps(newCaps)}
                    testid={`brand-alloc-cap-${u.assignee_id}`}
                  />
                </div>
                <div className="relative h-6 bg-muted/40 border border-border" style={{ width: `${Math.max(4, widthPct)}%` }}>
                  <div className="absolute inset-0 flex">
                    {cells.map((c) => (
                      <div
                        key={c.brand_id || "none"}
                        className="h-full flex items-center justify-center overflow-hidden text-white text-[10px] font-medium"
                        style={{ background: brandColor(c.brand_id), width: `${(c.monthly_hours / u.monthly_hours) * 100}%` }}
                        title={`${c.brand_name} · ${c.monthly_hours}h/mo · ${Object.entries(c.designations || {}).map(([d, h]) => `${d} ${h}h`).join(", ")}`}
                        data-testid={`brand-alloc-cell-${u.assignee_id}-${c.brand_id || "none"}`}
                      >
                        <span className="px-1 truncate">{c.brand_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <Sparkline points={trendPoints} months={trends?.months || []} capHours={u.cap_hours} testid={`brand-alloc-trend-${u.assignee_id}`} />
                <div className={`text-right text-xs font-mono font-semibold ${over ? "text-rose-800" : ""}`} data-testid={`brand-alloc-total-${u.assignee_id}`}>
                  {u.monthly_hours.toFixed(1)}h
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BrandLegend brandTotals={data.brand_totals} />
        <HistoryPanel events={history} />
      </div>
    </div>
  );
}

function SummaryTile({ label, value, icon, testid, tone = "" }) {
  const cls =
    tone === "amber" ? "border-amber-400/60 bg-amber-50" :
    tone === "rose" ? "border-rose-400/60 bg-rose-50" :
    "border-border";
  return (
    <Card className={`rounded-none ${cls}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">{icon}{label}</div>
        <div className="text-xl font-bold mt-1" data-testid={testid}>{value}</div>
      </CardContent>
    </Card>
  );
}

function BrandLegend({ brandTotals }) {
  if (!brandTotals || brandTotals.length === 0) return <div />;
  return (
    <Card className="rounded-none" data-testid="brand-alloc-legend">
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Brand totals</div>
        <div className="space-y-1">
          {brandTotals.map((b) => (
            <div key={b.brand_id || "none"} className="flex items-center gap-2 text-xs" data-testid={`brand-alloc-brand-${b.brand_id || "none"}`}>
              <div className="w-3 h-3" style={{ background: brandColor(b.brand_id) }} />
              <span className="flex-1 truncate">{b.brand_name}</span>
              <span className="font-mono font-semibold">{b.monthly_hours.toFixed(1)}h</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function HistoryPanel({ events }) {
  return (
    <Card className="rounded-none" data-testid="brand-alloc-history">
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Recent hand-offs</div>
        {(!events || events.length === 0) ? (
          <div className="text-[11px] text-muted-foreground italic">No allocation changes yet.</div>
        ) : (
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {events.slice(0, 20).map((e) => (
              <div key={e.id} className="text-[11px] flex items-center gap-2" data-testid={`brand-alloc-history-${e.id}`}>
                <Badge variant="outline" className="rounded-none text-[9px]">{e.designation || "—"}</Badge>
                <span className="text-muted-foreground truncate flex-1">
                  {e.prev_assignee_name || <em>none</em>} → <strong>{e.assignee_name || "unassigned"}</strong>
                </span>
                <span className="text-[10px] text-muted-foreground">{(e.changed_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * R86 · Compact inline SVG sparkline for a member's last-N-months hours.
 * Renders bars (not a line) so a single month of history still reads well;
 * bars touching or crossing `capHours` are tinted rose to flag burnout.
 */
function Sparkline({ points, months, capHours, testid }) {
  if (!points || points.length === 0) {
    return <div className="text-[9px] text-muted-foreground italic text-center" data-testid={`${testid}-empty`}>no history</div>;
  }
  const w = 110, h = 26, gap = 2;
  const bw = Math.max(3, (w - gap * (points.length - 1)) / points.length);
  const maxY = Math.max(1, capHours || 0, ...points.map((p) => p.hours || 0));
  return (
    <div className="flex flex-col items-center" data-testid={testid} title={points.map((p) => `${p.month}: ${p.hours.toFixed(1)}h${p.over_capacity ? " ⚠" : ""}`).join(" · ")}>
      <svg width={w} height={h} className="overflow-visible">
        {capHours ? (
          <line x1={0} y1={h - (capHours / maxY) * h} x2={w} y2={h - (capHours / maxY) * h}
                stroke="hsl(0,72%,55%)" strokeWidth={0.5} strokeDasharray="2 2" opacity={0.6} />
        ) : null}
        {points.map((p, i) => {
          const bh = ((p.hours || 0) / maxY) * h;
          const x = i * (bw + gap);
          const y = h - bh;
          const fill = p.over_capacity ? "hsl(0,72%,55%)" : "hsl(215,85%,55%)";
          return <rect key={p.month} x={x} y={y} width={bw} height={Math.max(1, bh)} fill={fill} opacity={0.85} />;
        })}
      </svg>
      <div className="text-[8px] text-muted-foreground mt-0.5 leading-none">
        {(months?.[0] || "").slice(2)} → {(months?.[months.length - 1] || "").slice(2)}
      </div>
    </div>
  );
}

/**
 * R86 · Per-member capacity cap editor. Compact "cap" chip; clicking it
 * expands into an inline number input + save/clear buttons. Empty input +
 * save clears the override (falls back to workspace default).
 */
function MemberCapEditor({ userId, override, fallbackCap, memberCaps, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (editing) setDraft(override != null ? String(override) : "");
  }, [editing, override]);
  const save = () => {
    const next = { ...(memberCaps || {}) };
    const raw = (draft || "").trim();
    if (raw === "") {
      delete next[userId];
    } else {
      const n = Math.max(0, Math.min(1000, Math.round(Number(raw))));
      if (!Number.isFinite(n)) { setEditing(false); return; }
      next[userId] = n;
    }
    onSave(next);
    setEditing(false);
  };
  if (editing) {
    return (
      <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
        <Input
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
          className="h-6 w-14 rounded-none text-[10px] px-1"
          placeholder="—"
          autoFocus
          data-testid={`${testid}-input`}
        />
        <Button type="button" size="sm" variant="outline" className="h-6 px-1 rounded-none" onClick={save} data-testid={`${testid}-save`}>
          <Check className="w-3 h-3" />
        </Button>
        <Button type="button" size="sm" variant="ghost" className="h-6 px-1 rounded-none" onClick={() => setEditing(false)} data-testid={`${testid}-cancel`}>
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`shrink-0 text-[10px] px-1.5 h-5 border ${override != null ? "border-primary text-primary" : "border-muted-foreground/30 text-muted-foreground"} hover:border-primary/60`}
      title={override != null ? `Override: ${override}h/mo` : fallbackCap ? `Uses workspace default (${fallbackCap}h)` : "No cap"}
      data-testid={`${testid}-edit`}
    >
      {override != null ? `${override}h` : "—"}
    </button>
  );
}
